/* eslint-disable */
// Row-wise production plan paste (Long Term Production Plan).
//
// The sheet planners keep is one row per (date, material): Date | ... | Material | ... | Planned Qty,
// often with a title row above the headers, subtotal rows between days and comma numbers
// ("23,400"). findProductFromList reads the product-wise grid the plan exports instead - one row
// per product, one column per day of the month - so this turns the former into the latter and the
// rest of the paste (step 1 totals, step 3 ticks, step 4 day quantities) runs unchanged.
import moment from 'moment';

const DATE_FORMATS = [
	'DD-MM-YY', 'DD-MM-YYYY', 'D-M-YY', 'D-M-YYYY',
	'DD/MM/YY', 'DD/MM/YYYY', 'D/M/YY', 'D/M/YYYY',
	'DD.MM.YY', 'DD.MM.YYYY',
	'DD-MMM-YY', 'DD-MMM-YYYY', 'D-MMM-YY', 'D-MMM-YYYY',
	'YYYY-MM-DD',
];

const DATE_HEADERS = ['date', 'production date', 'plan date'];
const SKU_HEADERS = ['material', 'sku code', 'skucode', 'sku', 'client variant id', 'material code'];
const QTY_HEADERS = ['planned qty', 'planned quantity', 'plan qty', 'planned'];

// Header cells can be wrapped onto two lines in Excel ("Planned\nQty").
const normHeader = (cell) => String(cell ?? '').replace(/\s+/g, ' ').trim().toLowerCase();

const findColumn = (headerRow, names) => {
	const normalized = headerRow.map(normHeader);
	for (const name of names) {
		const index = normalized.indexOf(name);
		if (index !== -1) return index;
	}
	return -1;
};

// Excel's clipboard is TSV, quoting any cell that holds a tab, quote or line break - so a
// wrapped header arrives as "Planned<newline>Qty" and a plain split on newlines tears that row
// in two.
export const parseClipboardGrid = (text = '') => {
	const grid = [];
	let row = [];
	let cell = '';
	let inQuotes = false;
	for (let i = 0; i < text.length; i++) {
		const ch = text[i];
		if (inQuotes) {
			if (ch === '"' && text[i + 1] === '"') {
				cell += '"';
				i++;
			} else if (ch === '"') {
				inQuotes = false;
			} else {
				cell += ch;
			}
		} else if (ch === '"' && cell === '') {
			inQuotes = true;
		} else if (ch === '\t') {
			row.push(cell);
			cell = '';
		} else if (ch === '\n' || ch === '\r') {
			if (ch === '\r' && text[i + 1] === '\n') i++;
			row.push(cell);
			grid.push(row);
			row = [];
			cell = '';
		} else {
			cell += ch;
		}
	}
	if (cell !== '' || row.length) {
		row.push(cell);
		grid.push(row);
	}
	return grid.filter((r) => r.some((c) => String(c).trim() !== ''));
};

// The header row is the first one naming a date, a material and a planned quantity column;
// anything above it (the sheet's title row) is ignored.
const findHeaderRow = (grid) => {
	const limit = Math.min(grid.length, 10);
	for (let i = 0; i < limit; i++) {
		const dateCol = findColumn(grid[i], DATE_HEADERS);
		const skuCol = findColumn(grid[i], SKU_HEADERS);
		const qtyCol = findColumn(grid[i], QTY_HEADERS);
		if (dateCol !== -1 && skuCol !== -1 && qtyCol !== -1) {
			return { headerIndex: i, dateCol, skuCol, qtyCol };
		}
	}
	return null;
};

export const isRowWisePlanSheet = (grid) => !!findHeaderRow(grid);

const parseDate = (value) => {
	const text = String(value ?? '').trim();
	if (!text) return null;
	// A date cell copied unformatted comes through as Excel's day serial.
	if (/^\d{5}$/.test(text)) {
		return moment('1899-12-30', 'YYYY-MM-DD').add(parseInt(text, 10), 'days');
	}
	const parsed = moment(text, DATE_FORMATS, true);
	return parsed.isValid() ? parsed : null;
};

const parseQty = (value) => {
	const qty = parseFloat(String(value ?? '').replace(/[,\s]/g, ''));
	return Number.isFinite(qty) ? qty : NaN;
};

// Returns the product-wise grid findProductFromList expects - headers
// ['S.No', 'SKU Code', '1', '2', ...] and one row per material with its quantity per day of the
// plan month (same material on the same day summed) - plus warnings for the rows left out.
export const pivotRowWisePlan = (grid, state) => {
	const header = findHeaderRow(grid);
	if (!header) return null;
	const { headerIndex, dateCol, skuCol, qtyCol } = header;

	// Same month base the plan's own date columns use (getHeadersandRows).
	const month = state?.planPeriod !== null && state?.planPeriod !== undefined
		? state.planPeriod
		: moment().month();
	const planMonth = moment().startOf('year').month(month);
	const daysInMonth = planMonth.daysInMonth();

	const qtyBySku = {};
	const skuOrder = [];
	const badDates = [];
	const badQtys = [];
	const otherMonth = [];

	grid.slice(headerIndex + 1).forEach((cells) => {
		const sku = String(cells[skuCol] ?? '').trim();
		const rawDate = String(cells[dateCol] ?? '').trim();
		// Subtotal / blank rows carry neither a material nor a date.
		if (!sku || !rawDate) return;

		const date = parseDate(rawDate);
		if (!date) {
			badDates.push(`${sku} (${rawDate})`);
			return;
		}
		const qty = parseQty(cells[qtyCol]);
		if (Number.isNaN(qty) || qty < 0) {
			badQtys.push(`${sku} on ${date.format('DD-MM-YYYY')} (${cells[qtyCol] ?? ''})`);
			return;
		}
		if (qty === 0) return;
		if (!date.isSame(planMonth, 'month')) {
			otherMonth.push(`${sku} on ${date.format('DD-MM-YYYY')}`);
			return;
		}

		if (!qtyBySku[sku]) {
			qtyBySku[sku] = {};
			skuOrder.push(sku);
		}
		const day = date.date();
		qtyBySku[sku][day] = (qtyBySku[sku][day] || 0) + qty;
	});

	const dayHeaders = Array.from({ length: daysInMonth }, (_, i) => String(i + 1));
	const headers = ['S.No', 'SKU Code', ...dayHeaders];
	const rows = skuOrder.map((sku, index) => [
		String(index + 1),
		sku,
		...dayHeaders.map((day) => String(qtyBySku[sku][day] || '')),
	]);

	const warnings = [];
	const list = (items) => `${items.slice(0, 5).join(', ')}${items.length > 5 ? ` +${items.length - 5} more` : ''}`;
	if (otherMonth.length) {
		warnings.push(`${otherMonth.length} row(s) dated outside ${planMonth.format('MMMM YYYY')} were skipped: ${list(otherMonth)}`);
	}
	if (badDates.length) {
		warnings.push(`${badDates.length} row(s) with an unreadable date were skipped: ${list(badDates)}`);
	}
	if (badQtys.length) {
		warnings.push(`${badQtys.length} row(s) with an invalid Planned Qty were skipped: ${list(badQtys)}`);
	}

	return { headers, rows, warnings };
};
